/**
 * Dependencies
 */
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';
import snakeCase from 'lodash/snakeCase';
import isEqual from 'lodash/isEqual';
import querystring from 'query-string';
import classNames from 'classnames';

import nds from '@nerdwallet/base-styles/classes';
import ConfigBuilder, { getMacroKey } from '@nerdwallet/react-config-builder';
import { Box, Button, Text } from '@nerdwallet/currency';
import ContentCopy from '@nerdwallet/react-icon/material/ContentCopy';
import Trash from '@nerdwallet/react-icon/gizmo/Trash';
import Up from '@nerdwallet/react-icon/gizmo/Up';
import Down from '@nerdwallet/react-icon/gizmo/Down';
import TextFieldsIcon from '@nerdwallet/react-icon/material/TextFields';
import ImageIcon from '@nerdwallet/react-icon/material/Image';
import ButtonIcon from '@nerdwallet/react-icon/material/CallToAction';
import ExpansionPanelsIcon from '@nerdwallet/react-icon/material/UnfoldMore';
import LinkIcon from '@nerdwallet/react-icon/material/Link';
import StarIcon from '@nerdwallet/react-icon/material/StarHalf';
import ListIcon from '@nerdwallet/react-icon/material/List';
import TooltipIcon from '@nerdwallet/react-icon/material/ChatBubble';
import Edit from '@nerdwallet/react-icon/material/Edit';
import Accordion from '@nerdwallet/react-accordion';
import { formSubmit } from '@nerdwallet/nw-client-lib/tracking';

import { ColorDropdown } from '~/app/lib/color-picker/color-picker';
import { makeRawUpdate } from '../../lib/redux/actions/main';
import {
  stringInputSpec,
  stringTextAreaSpec,
  stringSelectSpec,
  boolCheckboxSpec,
  repeatable,
  findPathInParent,
  generateSettingUIConfigFromSpec,
} from '../../lib/editing-configs';
import { getValidationFn } from '../../lib/validations';
import { valueFromConfigBuilderCheckbox } from '../../lib/helpers';

import { receiveForm, getRepeatablePath } from './actions';
import {
  addRepeatable,
  deleteRepeatable,
  duplicateRepeatable,
  duplicateGridItemComponent,
  duplicateDynamicGridObject,
  moveRepeatable,
} from '../../lib/redux/actions/builder';

import './grid-form.scss';
import { Draggable } from './draggable';
import { GroupButton } from './lib/group-button/group-button';
import { RenamePanel } from './lib/group-button/rename-panel';

// Static styles to avoid recreation on every render
const PANEL_TITLE_STYLE = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '19em',
};

const PANEL_TITLE_TEXT_STYLE = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textTransform: 'capitalize',
};

const getIcon = (componentType, options) => {
  if (componentType === 'text') {
    return <TextFieldsIcon size={20} />;
  }
  if (componentType === 'image') {
    return <ImageIcon size={20} />;
  }
  if (componentType === 'button') {
    return <ButtonIcon size={20} />;
  }
  if (componentType === 'accordion') {
    return <ExpansionPanelsIcon size={20} />;
  }
  if (componentType === 'link') {
    return <LinkIcon size={20} />;
  }
  if (componentType === 'star-rating') {
    return <StarIcon size={20} />;
  }
  if (componentType === 'bulletList') {
    return <ListIcon size={20} />;
  }
  if (componentType === 'tooltip') {
    return <TooltipIcon size={20} />;
  }
  return options?.icon ? <options.icon size={20} /> : null;
};

/*
 * Config-based, grid-stacking form.
 * If provided a redirectUrl, will stringify form fields and values and redirect to the url provided.
 * If provided an onClick, will pass form as an object to the onClick.
 * Current implementation holds form state internally.
 */
class GridForm extends Component {
  static withConfigBasedValidation(elConfig) {
    const { validationType } = elConfig;
    const keyToValidate = get(elConfig.keys, '0.key');
    const validationFn = getValidationFn(validationType, keyToValidate);

    return {
      getValidationConfig: validationFn,

      // Explicitly defined fn gets precedent
      ...elConfig,
    };
  }

  constructor(props) {
    super(props);

    // Data structure is set in props
    this.state = this.props.initialState;

    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.updateColorPicker = this.onColorPickerChange.bind(this);
    this.handleAttemptedSubmit = debounce(this.handleAttemptedSubmit, 100);

    // Cache for memoized calculations
    this.memoizedFormValidation = null;
    this.lastFormConfigRef = null;
    this.lastStateRef = null;

    // Pre-bind frequently used methods to avoid recreating functions
    this.handleRename = this.handleRename.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleReorder = this.handleReorder.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Only rerender if meaningful props or state have changed
    const propsChanged =
      !isEqual(this.props.formConfig, nextProps.formConfig) ||
      !isEqual(this.props.initialState, nextProps.initialState) ||
      this.props.liveUpdate !== nextProps.liveUpdate ||
      this.props.buttonText !== nextProps.buttonText ||
      this.props.buttonSubText !== nextProps.buttonSubText ||
      this.props.orientation !== nextProps.orientation ||
      this.props.columns !== nextProps.columns;

    const stateChanged = !isEqual(this.state, nextState);

    return propsChanged || stateChanged;
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.attemptedSubmit !== this.state.attemptedSubmit) {
      this.handleAttemptedSubmit();
    }

    const newFormConfig = !isEqual(prevProps.formConfig, this.props.formConfig);
    const newInitialState = !isEqual(
      prevProps.initialState,
      this.props.initialState
    );

    if (newFormConfig || newInitialState) {
      // Clear memoization cache when config changes
      this.memoizedFormValidation = null;
      this.lastFormConfigRef = null;
      this.lastStateRef = null;
      this.reloadState();
    }

    // Clear memoization cache when state changes significantly
    if (this.lastStateRef !== this.state) {
      this.memoizedFormValidation = null;
      this.lastStateRef = null;
    }
  }

  handleRename(key, index, newName) {
    this.setState((prevState) => {
      const newState = {
        ...prevState,
        renameKey: null,
      };
      set(newState, `${key}.${index}.componentName`, newName);
      return newState;
    });
  }

  handleCancel(e) {
    e.stopPropagation();
    this.setState((prevState) => ({
      ...prevState,
      renameKey: null,
    }));
  }

  handleReorder(key) {
    return (event) => {
      this.moveRepeatable(key, event.from, event.to)();
    };
  }

  handleEditClick(key, index) {
    return () => {
      this.setState((prevState) => ({
        ...prevState,
        renameKey: `${key}.${index}`,
      }));
    };
  }

  handleAttemptedSubmit() {
    const { form } = this.state;
    const { formName, formConfig, dispatch } = this.props;

    if (this.isFormValid()) {
      const { redirectUrl, onClick } = this.props;

      // Log submission
      formSubmit({
        formName,
        fieldName: 'complete',
        fieldValue: 'complete',
      });

      // Pass data to given onClick
      if (onClick) onClick(form);

      // Make raw updates for each field if needed
      formConfig.forEach((fieldConfig) => {
        if (fieldConfig.outputKey) {
          const formKey = get(fieldConfig, 'keys.0.key');
          const value = get(this.state, formKey);
          const storePath = fieldConfig.outputKey;

          dispatch(makeRawUpdate(storePath, value));
        }
      });

      // dispatch form data to forms store
      dispatch(receiveForm(formName, form));

      // Redirect to given url with stringified form params
      if (window && redirectUrl) {
        const query = querystring.stringify(form);
        window.location = `${redirectUrl}?${query}`;
      }
    } else {
      // Log incomplete submission attempt
      formSubmit({
        formName,
        fieldName: 'incomplete',
        fieldValue: `submitted ${Object.keys(form).join(', ')}`,
      });
    }
  }

  onChange(config, value) {
    const key = getMacroKey(config, 'value');
    const parsedValue =
      config.viewType === 'checkbox'
        ? valueFromConfigBuilderCheckbox(value)
        : value;

    // Batch setState calls to prevent multiple rerenders
    this.setState((prevState) => {
      const newState = set(cloneDeep(prevState), key, parsedValue);

      if (this.props.liveUpdate) {
        newState.attemptedSubmit = (prevState.attemptedSubmit || 0) + 1;
      }

      return newState;
    });
  }

  onClick() {
    // Let validations know the user is trying to submit the form so we can
    // verbosely invalidate empty required fields.
    // eslint-disable-next-line react/no-access-state-in-setstate
    this.setState({ attemptedSubmit: (this.state.attemptedSubmit || 0) + 1 });
  }

  onColorPickerChange(config, value) {
    const key = getMacroKey(config, 'value');

    this.setState((prevState) => {
      const newState = cloneDeep(prevState);
      set(newState, key, value);

      if (this.props.liveUpdate) {
        newState.attemptedSubmit = (prevState.attemptedSubmit || 0) + 1;
      }

      return newState;
    });
  }

  getAddRepeatableFn(key) {
    const path = getRepeatablePath(key, this.props.initialState);

    return () => {
      const nextRepeatableIndex = get(this.props.initialState, key, []).length;
      const defaultRepeatable =
        get(this.state, 'form.type') === 'GridForm'
          ? {
              viewType: 'input',
              formatType: 'none',
              subType: 'text',
              value: '{{0}}',
              keys: [
                {
                  key: `form.attribute-${nextRepeatableIndex}`,
                  format: 'string',
                },
              ],
            }
          : {};
      this.props.dispatch(addRepeatable(path, defaultRepeatable));
    };
  }

  getDeleteRepeatableFn(key, index) {
    const path = getRepeatablePath(key, this.props.initialState);
    return this.props.dispatch(deleteRepeatable(path, index));
  }

  getDuplicateRepeatableFn(key, index) {
    const path = getRepeatablePath(key, this.props.initialState);

    if (path.includes('itemComponents')) {
      return this.props.dispatch(duplicateGridItemComponent(path, index));
    }

    // Verifies if the selected path includes a dynamic grid object
    if (path?.[0]?.includes('DynamicGrid')) {
      return this.props.dispatch(duplicateDynamicGridObject(path, index));
    }

    return this.props.dispatch(duplicateRepeatable(path, index));
  }

  getRepeatableTitle({
    value,
    fields,
    index,
    singularLabel,
    options,
    repeatableName,
  }) {
    if (repeatableName) {
      return repeatableName;
    }
    if (typeof options.label === 'function') {
      const label = options.label(value);

      if (label) {
        return label;
      }
    } else if (typeof options.label === 'string' && value[options.label]) {
      return value[options.label];
    } else if (typeof value?.type === 'string') {
      return value?.type;
    }

    // we want to show the value of the first field in the repeatable as the title of the panel
    const firstKey = get(fields, '0.keys.0.key', '_');
    const firstField = get(this.state, firstKey);
    // but if the first field is another repeatable or the first field is empty, just show a generic title
    return firstField && typeof firstField === 'string'
      ? firstField
      : `${singularLabel} #${index + 1}`;
  }

  moveRepeatable(key, from, to) {
    const path = getRepeatablePath(key, this.props.initialState);
    return () => this.props.dispatch(moveRepeatable(path, from, to));
  }

  // Pre-bound methods to avoid creating new functions on every render
  isFormValid() {
    const { formConfig } = this.props;
    const data = this.state;

    // Memoize expensive validation calculation
    if (
      this.lastFormConfigRef === formConfig &&
      this.lastStateRef === data &&
      this.memoizedFormValidation !== null
    ) {
      return this.memoizedFormValidation;
    }

    const hydratedConfig = formConfig.map(GridForm.withConfigBasedValidation);

    // Any fields invalid?
    const isValid = !hydratedConfig.some(
      ({ getValidationConfig }) =>
        getValidationConfig && !getValidationConfig(data).isValid
    );

    // Cache the result
    this.memoizedFormValidation = isValid;
    this.lastFormConfigRef = formConfig;
    this.lastStateRef = data;

    return isValid;
  }

  reloadState() {
    this.setState(this.props.initialState);
  }

  // Remove forceRerendering method as it causes unnecessary rerenders
  // The component will rerender naturally when state changes

  repeatableUI(data, key, i) {
    const moveUp = this.moveRepeatable(key, i, i - 1);
    const moveDown = this.moveRepeatable(key, i, i + 1);
    const editClickHandler = this.handleEditClick(key, i);
    const duplicateHandler = () => this.getDuplicateRepeatableFn(key, i);
    const deleteHandler = () => this.getDeleteRepeatableFn(key, i);

    return (
      <div className="ui">
        <Edit size={15} onClick={editClickHandler} />
        {i > 0 ? <Up onClick={moveUp} /> : null}
        {i < data.length - 1 ? <Down onClick={moveDown} /> : null}
        <ContentCopy
          onClick={duplicateHandler}
          size={15}
          title="duplicate this element"
          className="repeat"
        />
        <Trash
          onClick={deleteHandler}
          size={15}
          title="delete this element"
          className="trash"
        />
      </div>
    );
  }

  renderRepeatableGroup({ label, singularLabel, elements, keys, options }) {
    const { key } = keys[0];
    const data = get(this.state, key, []);
    const repeatableIsEditable = get(options, 'repeatableIsEditable', true);

    return (
      <div className="grid-form-repeatable" key={`repeatable-${key}`}>
        <Text bold size="small">
          {label}
        </Text>
        <Draggable reorder={this.handleReorder(key)}>
          {data.map((item, index) => {
            const processedElements = elements(index).map(
              generateSettingUIConfigFromSpec
            );
            const fields = processedElements.map(findPathInParent(key, index));

            const panelTitle = this.getRepeatableTitle({
              value: item,
              fields,
              index,
              singularLabel,
              options: {
                ...options,
                getKey: options?.getKey ? options?.getKey(key) : () => {},
              },
              repeatableName: item?.componentName,
            });

            const componentType = item?.componentType;
            const panelKey = `panel-${label}-${item?.$unique_key || index}`;

            return (
              <Fragment key={panelKey}>
                <Accordion
                  items={[
                    {
                      id: panelKey,
                      title:
                        get(this.state, 'renameKey') === `${key}.${index}` ? (
                          <RenamePanel
                            currentValue={panelTitle}
                            handleRename={(newName) =>
                              this.handleRename(key, index, newName)
                            }
                            handleCancel={this.handleCancel}
                          />
                        ) : (
                          <Box
                            display="flex"
                            alignItems="center"
                            style={PANEL_TITLE_STYLE}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              marginRight={2}
                            >
                              {getIcon(componentType, options)}
                            </Box>
                            <span style={PANEL_TITLE_TEXT_STYLE}>
                              {panelTitle}
                            </span>
                          </Box>
                        ),
                      children: (
                        <Fragment>
                          {repeatableIsEditable
                            ? this.repeatableUI(data, key, index)
                            : null}
                          {this.renderForm(fields)}
                        </Fragment>
                      ),
                    },
                  ]}
                />
              </Fragment>
            );
          })}
        </Draggable>
        {repeatableIsEditable ? (
          <Fragment>
            <Button
              className={nds('margin-bottom-3')}
              onClick={this.getAddRepeatableFn(key)}
              size="small"
              block
            >
              Add {singularLabel}
            </Button>
          </Fragment>
        ) : null}
      </div>
    );
  }

  renderGroup({ label, elements, options }, index) {
    return (
      <div className="grid-form-group" key={`${snakeCase(label)}-${index}`}>
        <Accordion
          key={`${snakeCase(label)}-${index}`}
          id={`${snakeCase(label)}-${index}`}
          title={label}
          items={[
            {
              title: label,
              id: `${snakeCase(label)}-${index}`,
              children: this.renderForm(
                elements.map((configElements) =>
                  generateSettingUIConfigFromSpec({
                    ...configElements,
                    fieldType: configElements?.fieldType || 'group',
                  })
                )
              ),
              initiallyExpanded: !!options?.expanded,
            },
          ]}
        />
      </div>
    );
  }

  renderWrapper({ elements }) {
    return this.renderForm(elements.map(generateSettingUIConfigFromSpec));
  }

  renderRow({ elements }) {
    // TODO: Not implemented
    return (
      <Box>
        {this.renderForm(elements.map(generateSettingUIConfigFromSpec))}
      </Box>
    );
  }

  renderCollapsable({ elements, keys, label, options }) {
    const { key } = keys[0];
    const fields = elements.map((element, index) => {
      const processedElement = generateSettingUIConfigFromSpec(element);
      return findPathInParent(key, index)(processedElement);
    });

    if (options?.getKey && typeof options?.getKey === 'function') {
      options?.getKey(key);
    }

    return (
      <Accordion
        key={key}
        id={key}
        items={[
          {
            title: (
              <Box display="flex" alignItems="center">
                <Box display="flex" alignItems="center" marginRight={2}>
                  {options?.icon ? <options.icon size={20} /> : null}
                </Box>
                {label}
              </Box>
            ),
            children: this.renderForm(fields),
          },
        ]}
      />
    );
  }

  renderGroupButton({ elements, ...rest }) {
    const fieldPath = rest?.keys?.[0]?.key;
    const handleGroupButton = (selectedValues) =>
      this.onChange(rest, { ...selectedValues });

    return (
      <GroupButton
        buttons={elements}
        initState={get(this.props.initialState, fieldPath)}
        handle={handleGroupButton}
      />
    );
  }

  renderColorPicker(config) {
    const fieldName = config?.keys?.[0]?.key;
    const initialColor = get(this.state, fieldName);
    const handleColorChange = (color) =>
      this.onColorPickerChange(config, color);

    return (
      <ColorDropdown
        label={config?.label}
        initialColor={initialColor}
        onChange={handleColorChange}
        fieldName={fieldName}
      />
    );
  }

  renderForm(formConfig) {
    return formConfig
      .filter((elConfig) => typeof elConfig.viewType !== 'undefined')
      .map((elConfig, index) => {
        const getUniqueKey = (type) =>
          `${type}-${get(elConfig, 'keys.0.key', index)}-${index}`;

        switch (elConfig.viewType) {
          case 'group': {
            return (
              <Fragment key={getUniqueKey('group')}>
                {this.renderGroup(elConfig, index)}
              </Fragment>
            );
          }
          case 'wrapper': {
            return (
              <Fragment key={getUniqueKey('wrapper')}>
                {this.renderWrapper(elConfig, index)}
              </Fragment>
            );
          }
          case 'repeatable': {
            return (
              <Fragment key={getUniqueKey('repeatable')}>
                {this.renderRepeatableGroup(elConfig)}
              </Fragment>
            );
          }
          case 'row': {
            return (
              <Fragment key={getUniqueKey('row')}>
                {this.renderRow(elConfig)}
              </Fragment>
            );
          }
          case 'groupButton': {
            return (
              <Fragment key={getUniqueKey('groupButton')}>
                {this.renderGroupButton(elConfig)}
              </Fragment>
            );
          }
          case 'collapsable': {
            return (
              <Fragment key={getUniqueKey('collapsable')}>
                {this.renderCollapsable(elConfig)}
              </Fragment>
            );
          }
          case 'colorPicker': {
            return (
              <Fragment key={getUniqueKey('colorPicker')}>
                {this.renderColorPicker(elConfig)}
              </Fragment>
            );
          }
          default: {
            return (
              <ConfigBuilder
                data={this.state}
                className={elConfig.viewType}
                viewConfig={GridForm.withConfigBasedValidation(elConfig)}
                onChange={this.onChange}
                formName={this.props.formName}
                // eslint-disable-next-line react/no-array-index-key
                key={`${get(elConfig, 'viewType')}-${index}`}
              />
            );
          }
        }
      });
  }

  renderButton() {
    const { buttonText, buttonSubText } = this.props;
    const disabled = !this.isFormValid();

    return (
      <div>
        <Button primary block onClick={this.onClick} disabled={disabled}>
          {buttonText}
        </Button>
        <div className="grid-form-button-subtext">{buttonSubText}</div>
      </div>
    );
  }

  render() {
    const { formConfig, liveUpdate, orientation, columns } = this.props;

    return (
      <div className="grid-form">
        <div
          className={classNames('grid-form-container', {
            'grid-form-container-1-col': orientation === 'vertical',
            [`grid-form-container-${columns}-col`]: orientation !== 'vertical',
          })}
        >
          {this.renderForm(formConfig)}
          {!liveUpdate && this.renderButton()}
        </div>
      </div>
    );
  }
}

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
GridForm.displayName = 'GridForm';

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
GridForm.propTypes = {
  buttonSubText: PropTypes.string,
  buttonText: PropTypes.string,
  columns: PropTypes.string,

  // Field configs based on ConfigBuilder API, with a few modifications
  formConfig: PropTypes.arrayOf(
    PropTypes.shape({
      // The key on the store you want this field to control.
      // If this key is wiped out by a reducer function, the value will not persist.
      outputKey: PropTypes.string,

      /* All following props are ala ConfigBuilder */

      // Input type
      viewType: PropTypes.oneOf([
        'input',
        'radio',
        'select',
        'checkbox',
        'textarea',
        'repeatable',
        'group',
        'groupButton',
        'wrapper',
        'colorPicker',
        // There are more types, but not yet supported in editor
      ]),

      // Shown above input
      label: PropTypes.string,

      // Renders help tooltip
      help: PropTypes.string,

      // Renders placeholder
      placeholder: PropTypes.string,

      // How data should be rendered in the input
      formatType: PropTypes.oneOf([
        'currency',
        'text',
        'none',
        'number',
        'date',
      ]),

      // How data should be stored
      subType: PropTypes.oneOf(['currency', 'text', 'number', 'date']),

      // Populated by getMacroKey. Typically {{0}}, ie first macro value. Don't mess with this.
      value: PropTypes.string,

      // Select menu options
      items: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool,
          ]),
        })
      ),

      // Keys on the form state that this form should control, and their data format
      keys: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string,
          format: PropTypes.oneOf(['text', 'currency', 'number', 'date']),
        })
      ),

      // See ConfigBuilder
      getValidationConfig: PropTypes.func,

      // Used to set `getValidationConfig` (above) in a config-based way.
      // Provide a type of validation (e.g. 'zip') and that validation will be used
      // if it is available in the validations lib.
      validationType: PropTypes.string,
    })
  ),
  // eslint-disable-next-line react/require-default-props
  formName: PropTypes.string,
  initialState: PropTypes.object,
  liveUpdate: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  onClick: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  redirectUrl: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  dispatch: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  orientation: PropTypes.oneOf(['vertical', 'horizontal']),
};

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
GridForm.defaultProps = {
  initialState: {},
  buttonText: 'Click me',
  buttonSubText: '',
  formConfig: [],
  liveUpdate: false,
  columns: '3',
};

GridForm.getEditingConfig = () => [
  stringInputSpec('formName', 'Form Name (for tracking)', {
    help: `E.g., cd-table-filter, or quotacy-data. Name can be simple,
    since you'll filter by url then by form name.`,
  }),
  boolCheckboxSpec('liveUpdate', 'Auto Submit?', {
    help: `Click this if you want to hide the submit button.
    The form will submit as soon as it is valid.
    E.g. use case: if this form is a filter for something else.`,
  }),
  stringInputSpec('buttonText', 'Button text'),
  stringInputSpec('buttonSubText', 'Button subtext'),
  stringSelectSpec(
    'columns',
    'Number of columns',
    [
      {
        value: '4',
        label: '4',
      },
      {
        value: '3',
        label: '3',
      },
      {
        value: '1',
        label: '1',
      },
    ],
    {
      help: 'Number of columns in layout on desktop',
    }
  ),
  repeatable('formConfig', 'Fields', 'Field', () => [
    stringInputSpec('title', 'Field Label'),
    stringTextAreaSpec('help', 'Help Bubble'),
    stringSelectSpec(
      'viewType',
      'Input Type',
      [
        {
          value: '',
          label: 'Select One',
        },
        {
          value: 'input',
          label: 'Input',
        },
        {
          value: 'select',
          label: 'Select',
        },
        {
          value: 'textarea',
          label: 'Text Area',
        },
        {
          value: 'checkbox',
          label: 'Checkbox',
        },
        {
          value: 'radio',
          label: 'Radio',
        },
      ],
      {
        help: 'Type of UI for this field',
      }
    ),
    stringSelectSpec(
      'formatType',
      'Show as...',
      [
        {
          value: '',
          label: 'Select One',
        },
        {
          value: 'none',
          label: 'Text',
        },
        {
          value: 'currency',
          label: 'Currency',
        },
        {
          value: 'number',
          label: 'Number',
        },
        {
          value: 'date',
          label: 'Date',
        },
      ],
      {
        help: 'How do you want this field to be formatted?',
      }
    ),
    stringSelectSpec(
      'subType',
      'Store data as...',
      [
        {
          value: '',
          label: 'Select one',
        },
        {
          value: 'text',
          label: 'Text',
        },
        {
          value: 'number',
          label: 'Number',
        },
        {
          value: 'date',
          label: 'Date',
        },
      ],
      {
        help: 'How do you want the data to actually be stored (vs displayed)?',
      }
    ),
    repeatable('items', 'Options (for select)', 'Option', () => [
      stringInputSpec('label', 'Label', {
        help: 'What shows for this option',
      }),
      stringInputSpec('value', 'Value', {
        help: 'What is stored for this option',
      }),
    ]),
    repeatable('keys', 'Form Key', 'Key', () => [
      stringInputSpec('key', 'Key', {
        help: `The component state key to use for storing the data.
        Namespace under 'form.<key>' unless you need to do otherwise.
        E.g. form.zip`,
      }),
      stringSelectSpec(
        'format',
        'Format',
        [
          {
            value: 'currency',
            label: 'Currency',
          },
          {
            value: 'number',
            label: 'Number',
          },
          {
            value: 'string',
            label: 'Text',
          },
          {
            value: 'date',
            label: 'Date',
          },
        ],
        {
          help: 'How this value should be stored (again.)',
        }
      ),
    ]),
    stringInputSpec('outputKey', 'Store Key', {
      help: `If you want to control a value on the redux store, enter the key
      here. Note, a reducer can still overwrite or remove your value.`,
    }),
  ]),
];

export default connect()(React.memo(GridForm));
